/**
 * The assistant panel's store and its one-shot prefill (mission 8 WP07 T034;
 * A8, OR-9, amendment 14; FR-013, FR-025; research §1.20; contracts/ui-api.md
 * "Spine"; squad S19, C18, U2, U4, U21; post-tasks TR-6, R14, R24, E5).
 *
 * - **Memory only.** `AssistantPanelProvider` holds `{ruleContext, prefill,
 *   draft}` in React state for one run's screen; nothing of it reaches the URL,
 *   storage or a log. Filter, tab and drawer writes do not touch it (U4). A new
 *   `runId` starts empty.
 * - **URL.** `?assistant=open` and nothing else (A8): `open` is
 *   `searchParams.get('assistant') === 'open'`. `openPanel` navigates once,
 *   applying the caller's `edit` to a copy of the search params (the drawer's
 *   close, E5), with `replace` when asked (TR-6), else a push. `closePanel` pops
 *   an open this session pushed, and replaces otherwise, or when the search
 *   changed since the push, so a filter written while open is kept (U21.3).
 * - **Cross-route prefill (R14, R24).** `assistantNavState(prefill)` is the only
 *   way to put a prefill in router state. The provider reads
 *   `location.state.assistant`, keeps only the typed fields that validate
 *   (`ruleId` `^[A-Z0-9-]{1,32}$`, a non-negative integer `count`, a layout v1
 *   `column`), then replaces the entry with `state: null`, so the prefill
 *   survives neither a reload nor Back (OR-9, C18). A consumed `kind: 'rule'`
 *   prefill also sets the rule context (analysis U2).
 * - **No user text travels.** A prefill is typed data; the composer builds the
 *   question from the register (S19). `assistant-panel.test.tsx` scans `src/**`
 *   for any other router state that mentions the assistant or carries a draft.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { idSegment } from './api'

/** What an entry point hands the composer: typed fields only, never free text (OR-9, S19). */
export type AssistantPrefill =
  | { kind: 'rule'; ruleId: string; count: number }
  | { kind: 'column'; column: string; count: number }
  | { kind: 'findings'; count: number }

export interface OpenPanelOptions {
  /** Replace the current entry instead of pushing one (the drawer hand-off, TR-6). */
  replace?: boolean
  prefill?: AssistantPrefill
  /** The rule context every ask carries until removed (OR-9). */
  rule?: string
  /** A pure edit of a copy of the search params, applied in the same navigation (E5). */
  edit?: (params: URLSearchParams) => void
}

export interface AssistantPanel {
  open: boolean
  ruleContext: string | null
  prefill: AssistantPrefill | null
  draft: string
  setDraft: (draft: string) => void
  openPanel: (opts?: OpenPanelOptions) => void
  closePanel: () => void
  clearRuleContext: () => void
  /** The pending prefill, handed over once; the store forgets it. */
  consumePrefill: () => AssistantPrefill | null
}

/**
 * Layout v1's 52 columns (backend/internal/domain/layout/v1.go, in order), the only columns a
 * cross-route prefill may name (R24). `assistant-panel.test.tsx` reads v1.go and compares.
 */
export const LAYOUT_V1_COLUMNS: ReadonlySet<string> = new Set([
  'association_code',
  'first_name',
  'middle_name',
  'last_name',
  'address_line_1',
  'address_line_2',
  'city',
  'state',
  'zip_code',
  'social_security_number',
  'phone_number',
  'date_of_birth',
  'consumer_information_indicator',
  'joint_association_code',
  'joint_first_name',
  'joint_middle_name',
  'joint_last_name',
  'joint_address_line_1',
  'joint_address_line_2',
  'joint_city',
  'joint_state',
  'joint_zip_code',
  'joint_social_security_number',
  'joint_phone_number',
  'joint_date_of_birth',
  'joint_consumer_information_indicator',
  'customer_account_number',
  'portfolio_type',
  'account_type',
  'date_opened',
  'date_of_first_delinquency',
  'date_of_last_payment',
  'date_closed',
  'date_of_account_information',
  'account_status',
  'payment_rating',
  'special_comment_code',
  'compliance_condition_code',
  'credit_limit',
  'highest_credit',
  'current_balance',
  'amount_past_due',
  'scheduled_monthly_payment',
  'actual_payment',
  'terms_frequency',
  'terms_duration',
  'original_charge_off_amount',
  'payment_history_profile',
  'original_creditor_name',
  'creditor_classification',
  'purchased_from_or_sold_to_name',
  'purchased_from_or_sold_to_indicator',
])

const RULE_ID = /^[A-Z0-9-]{1,32}$/
const PARAM = 'assistant'

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
}

/** R24: a router-state prefill, rebuilt from its validated fields only; anything else is null. */
export function parsePrefill(raw: unknown): AssistantPrefill | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (!isCount(r['count'])) return null
  const count = r['count']
  if (r['kind'] === 'findings') return { kind: 'findings', count }
  if (r['kind'] === 'rule' && typeof r['ruleId'] === 'string' && RULE_ID.test(r['ruleId'])) {
    return { kind: 'rule', ruleId: r['ruleId'], count }
  }
  if (r['kind'] === 'column' && typeof r['column'] === 'string' && LAYOUT_V1_COLUMNS.has(r['column'])) {
    return { kind: 'column', column: r['column'], count }
  }
  return null
}

/** The only router-state transport for a prefill (R14): `navigate(to, { state: assistantNavState(p) })`. */
export function assistantNavState(prefill: AssistantPrefill): { assistant: AssistantPrefill } {
  return { assistant: { ...prefill } }
}

/** The run's page with the panel open. */
export function assistantHref(runId: string): string {
  return `/app/runs/${idSegment(runId, 'run id')}?${PARAM}=open`
}

/** `search` without the assistant param, as `?a=b` or `''`. */
export function withoutAssistantParam(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(PARAM)
  const rest = params.toString()
  return rest === '' ? '' : `?${rest}`
}

const Context = createContext<AssistantPanel | null>(null)

export function AssistantPanelProvider({ runId, children }: { runId: string; children: ReactNode }) {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const open = searchParams.get(PARAM) === 'open'

  const [scope, setScope] = useState(runId)
  const [ruleContext, setRuleContext] = useState<string | null>(null)
  const [prefill, setPrefill] = useState<AssistantPrefill | null>(null)
  const [draft, setDraft] = useState('')
  if (scope !== runId) {
    setScope(runId)
    setRuleContext(null)
    setPrefill(null)
    setDraft('')
  }

  // The search the panel was pushed from, while this session's push is the current entry (U21.3).
  const pushedFrom = useRef<string | null>(null)
  useEffect(() => {
    if (!open) pushedFrom.current = null
  }, [open])

  // Consume a cross-route prefill once, then replace the entry's state with null (OR-9, C18).
  useEffect(() => {
    const state = location.state as { assistant?: unknown } | null
    if (state === null || typeof state !== 'object' || !('assistant' in state)) return
    const next = parsePrefill(state.assistant)
    if (next !== null) {
      setPrefill(next)
      if (next.kind === 'rule') setRuleContext(next.ruleId)
    }
    navigate(location, { replace: true, state: null })
  }, [location, navigate])

  const openPanel = useCallback(
    (opts: OpenPanelOptions = {}) => {
      const rule = opts.rule ?? (opts.prefill?.kind === 'rule' ? opts.prefill.ruleId : undefined)
      if (rule !== undefined) setRuleContext(rule)
      if (opts.prefill !== undefined) setPrefill(opts.prefill)
      if (open && opts.edit === undefined) return
      const params = new URLSearchParams(location.search)
      opts.edit?.(params)
      params.set(PARAM, 'open')
      const replace = opts.replace === true || open
      if (!replace) pushedFrom.current = withoutAssistantParam(params.toString())
      navigate({ pathname: location.pathname, search: `?${params.toString()}`, hash: location.hash }, { replace })
    },
    [open, location.pathname, location.search, location.hash, navigate],
  )

  const closePanel = useCallback(() => {
    const rest = withoutAssistantParam(location.search)
    if (pushedFrom.current !== null && pushedFrom.current === rest) {
      pushedFrom.current = null
      navigate(-1)
      return
    }
    pushedFrom.current = null
    navigate({ pathname: location.pathname, search: rest, hash: location.hash }, { replace: true })
  }, [location.pathname, location.search, location.hash, navigate])

  const clearRuleContext = useCallback(() => setRuleContext(null), [])

  const prefillRef = useRef(prefill)
  prefillRef.current = prefill
  const consumePrefill = useCallback(() => {
    const taken = prefillRef.current
    prefillRef.current = null
    setPrefill(null)
    return taken
  }, [])

  const value = useMemo<AssistantPanel>(
    () => ({ open, ruleContext, prefill, draft, setDraft, openPanel, closePanel, clearRuleContext, consumePrefill }),
    [open, ruleContext, prefill, draft, openPanel, closePanel, clearRuleContext, consumePrefill],
  )
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useAssistantPanel(): AssistantPanel {
  const panel = useContext(Context)
  if (panel === null) throw new Error('useAssistantPanel needs an AssistantPanelProvider')
  return panel
}
